import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanDeactivate, NavigationCancel, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { RenderFlowService } from './render-flow.service';
import { RENDER_LANDING_URL } from './render-home';
import { RenderUploadStepComponent } from './steps/render-upload-step.component';

const AUTH_WAIT_MS = 4000;
// A step URL opened in a new tab (a link from an email, another device) waits this long for the
// backend's list of the user's jobs before calling the model unknown.
const LIST_WAIT_MS = 6000;

// The flow (upload and every step) is for signed-in users. A guest lands on the landing with
// ?next=<the url they wanted>: the landing opens the sign-in popup and continues there once
// they are in. On a cold load Firebase needs a moment to restore the session, so the guard
// waits for it the way AdminGuard does.
@Injectable({ providedIn: 'root' })
export class RenderSignedInGuard implements CanActivate {
  constructor(
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly router: Router,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    await waitForAuth(this.firebaseAuth);
    if (this.firebaseAuth.currentUser) {
      return true;
    }
    return this.router.createUrlTree([RENDER_LANDING_URL], { queryParams: { next: state.url } });
  }
}

// Back from Stripe's checkout at the site's root (/success?session_id=…, /cancel — the addresses
// purchases-back gives Stripe): a render model's checkout goes back into the flow, its confirming
// step or its preview (RenderFlowService.checkoutReturnUrl). Any other return goes where these
// addresses always led, the app's home (the web routes' '**').
@Injectable({ providedIn: 'root' })
export class RenderCheckoutReturnGuard implements CanActivate {
  constructor(
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly flow: RenderFlowService,
    private readonly router: Router,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<UrlTree> {
    const paid = route.routeConfig?.path === 'success';
    await waitForAuth(this.firebaseAuth);
    const url = this.firebaseAuth.currentUser ? await this.flow.checkoutReturnUrl(paid, route.queryParamMap.get('session_id') || '') : null;
    return url ? this.router.parseUrl(url) : this.router.createUrlTree([''], { queryParams: route.queryParams });
  }
}

// Resolves once Firebase has restored the session (or found none), or after AUTH_WAIT_MS.
function waitForAuth(firebaseAuth: FirebaseAuthService): Promise<void> {
  if (firebaseAuth.currentUser) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timeoutId);
      signedIn.unsubscribe();
      signedOut.unsubscribe();
      resolve();
    };
    const timeoutId = setTimeout(done, AUTH_WAIT_MS);
    const signedIn = firebaseAuth.firebaseUser.subscribe(() => done());
    const signedOut = firebaseAuth.notLoggedUserSubscription.subscribe(() => done());
  });
}

// Step routes carry a model id. Unknown model (another session, a typo) → the upload step with a
// note; a step the model is not on → the step it is on (the prototype's "Preview this model
// before opening its download pack").
@Injectable({ providedIn: 'root' })
export class RenderJobGuard implements CanActivate {
  constructor(
    private readonly flow: RenderFlowService,
    private readonly router: Router,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const modelId = route.paramMap.get('modelId') || '';
    const step = route.routeConfig?.path?.split('/')[0] || '';
    let job = this.flow.job(modelId);
    if (!job && this.flow.hasBackendList) {
      await this.flow.whenListed(LIST_WAIT_MS);
      job = this.flow.job(modelId);
    }

    if (!job) {
      this.flow.setNotice('That model is not in this session any more. Start with a new one.');
      return this.router.parseUrl('/render/upload');
    }
    if (job.step !== step) {
      if (step === 'downloads') {
        this.flow.setNotice('Preview this model before opening its downloads.');
      }
      return this.router.parseUrl(RenderFlowService.stepUrl(job));
    }
    return true;
  }
}

// Leaving the upload step while an archive is being unpacked drops it (the designer's rule for the
// upload, 2026-10-16 — the upload itself goes on wherever the user goes since 2026-10-20): the
// step asks first (RenderUploadStepComponent.confirmLeave). Every way out within the site comes
// through here — the step's chevron, Back to home, the profile menu, and the browser's Back, a
// route change in this app rather than an unload, so beforeunload never sees it.
//
// Back has already moved the browser one entry back when the question is asked. Staying, the
// router writes this page's address over that entry ('replace', the app's setting): the entry
// before it is put back and this page pushed after it, so Back still leads where it did.
@Injectable({ providedIn: 'root' })
export class RenderUploadLeaveGuard implements CanDeactivate<RenderUploadStepComponent> {
  constructor(
    private readonly router: Router,
    private readonly location: Location,
  ) {}

  async canDeactivate(step: RenderUploadStepComponent, route: ActivatedRouteSnapshot, current: RouterStateSnapshot, next?: RouterStateSnapshot): Promise<boolean> {
    if (!step.uploadInFlight) {
      return true;
    }
    const navigation = this.router.getCurrentNavigation();
    const leave = await step.confirmLeave();
    if (!leave && navigation?.trigger === 'popstate' && next) {
      this.router.events
        .pipe(
          filter((event): event is NavigationCancel => event instanceof NavigationCancel && event.id === navigation.id),
          take(1),
        )
        .subscribe(() => {
          this.location.replaceState(next.url);
          this.location.go(current.url);
        });
    }
    return leave;
  }
}
